import { useCallback, useEffect, useRef, useState } from 'react'
import {
  COUNTDOWN_SECONDS,
  VERSION_POLL_MS,
  VERSION_STORAGE_KEY,
} from './constants'

function formatCountdown(remaining: number): string {
  const mins = Math.floor(remaining / 60)
  const secs = remaining % 60
  const minPart = mins ? `${mins}m ` : ''
  return `${minPart}${secs}s`
}

/**
 * Angular `AppComponent.checkUpdatedVersion` / `updateVersion` parity:
 * poll `/commit.txt`, compare to `sessionStorage.app_versionid`, show banner + 5m countdown.
 */
export function useSoftwareUpdateCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [remainingSeconds, setRemainingSeconds] = useState(COUNTDOWN_SECONDS - 1)
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const countdownStartedRef = useRef(false)

  const applyUpdate = useCallback(() => {
    try {
      sessionStorage.removeItem(VERSION_STORAGE_KEY)
    } catch {
      // ignore
    }
    const url = new URL(window.location.href)
    const sep = url.searchParams.toString() ? '&' : '?'
    window.location.href = `${window.location.href}${sep}updated=${Date.now()}`
  }, [])

  const startCountdown = useCallback(() => {
    if (countdownStartedRef.current) return
    countdownStartedRef.current = true
    setRemainingSeconds(COUNTDOWN_SECONDS - 1)
    let count = 1
    countdownRef.current = setInterval(() => {
      count += 1
      setRemainingSeconds(COUNTDOWN_SECONDS - count)
      if (count >= COUNTDOWN_SECONDS) {
        if (countdownRef.current) clearInterval(countdownRef.current)
        applyUpdate()
      }
    }, 1000)
  }, [applyUpdate])

  const checkUpdatedVersion = useCallback(() => {
    const headers = new Headers()
    headers.append('pragma', 'no-cache')
    headers.append('cache-control', 'no-cache')
    void fetch(`/commit.txt?_=${Date.now()}`, { method: 'GET', headers, cache: 'no-store' })
      .then((response) => {
        if (!response.ok) return null
        return response.text()
      })
      .then((text) => {
        if (text == null) return
        const version = text.trim()
        if (!version) return
        let stored: string | null = null
        try {
          stored = sessionStorage.getItem(VERSION_STORAGE_KEY)
        } catch {
          stored = null
        }
        if (stored && stored !== version) {
          try {
            sessionStorage.setItem(VERSION_STORAGE_KEY, version)
          } catch {
            // ignore
          }
          setUpdateAvailable(true)
          startCountdown()
          return
        }
        if (!stored) {
          try {
            sessionStorage.setItem(VERSION_STORAGE_KEY, version)
          } catch {
            // ignore
          }
        }
      })
      .catch(() => {
        // Angular ignores fetch errors
      })
  }, [startCountdown])

  useEffect(() => {
    checkUpdatedVersion()
    pollRef.current = setInterval(checkUpdatedVersion, VERSION_POLL_MS)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
      if (countdownRef.current) clearInterval(countdownRef.current)
    }
  }, [checkUpdatedVersion])

  return {
    updateAvailable,
    remainingLabel: formatCountdown(Math.max(0, remainingSeconds)),
    applyUpdate,
  }
}
